/*
 * Thunderstorm is a full web app framework!
 *
 * Typescript & Express backend infrastructure that natively runs on firebase function
 * Typescript & React frontend infrastructure
 *
 * Copyright (C) 2020 Adam van der Kruk aka TacB0sS
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Created by tacb0ss on 27/07/2018.
 */
import { _keys, deepClone, exists, merge, Module, resolveContent } from '@nu-art/ts-common';
import { ThunderDispatcher } from '../core/thunder-dispatcher.js';
export class StorageModule_Class extends Module {
    cache = {};
    withstandDeletionKeys = [];
    allKeys = {};
    init() {
        window.addEventListener('storage', this.handleStorageEvent);
    }
    async __onClearWebsiteData() {
        const items = this.withstandDeletionKeys.map(key => key.get());
        localStorage.clear();
        sessionStorage.clear();
        _keys(this.cache).forEach(key => delete this.cache[key]);
        this.withstandDeletionKeys.forEach((key, index) => {
            if (items[index])
                key.set(items[index]);
        });
    }
    handleStorageEvent = async (e) => {
        const key = e.key;
        if (!key)
            return this.logVerbose('storage event without any key');
        const storageKey = this.allKeys[key];
        if (!storageKey)
            return this.logVerbose(`no StorageKey for '${key}'`);
        this.logDebug(`Storage key '${key}' was updated`, e.oldValue, e.newValue);
        try {
            // @ts-ignore
            await storageKey._onChange?.();
        }
        catch (e) {
            this.logError(`Error while handling change event for storage key: ${storageKey.key}`);
            this.logError(e);
            throw e;
        }
        const dispatcher = new ThunderDispatcher('__onStorageKeyEvent');
        await dispatcher.dispatchModuleAsync(e);
        await dispatcher.dispatchUIAsync(e);
    };
    getStorage = (persist) => persist ? localStorage : sessionStorage;
    set(key, value, persist = true) {
        if (value === undefined)
            return this.delete(key);
        if (this.cache[key] === value)
            return;
        this.cache[key] = value;
        this.getStorage(persist).setItem(key, JSON.stringify(value));
        // @ts-ignore
        this.allKeys[key]._onChange?.();
    }
    delete(key, persist = true) {
        this.clearCache(key);
        this.getStorage(persist).removeItem(key);
        // @ts-ignore
        this.allKeys[key]._onChange?.();
    }
    clearCache(key) {
        delete this.cache[key];
    }
    get(key, fallbackValue, persist = true, sameInstance = false) {
        const returnValue = (value) => {
            return sameInstance ? value : deepClone(value);
        };
        if (this.cache[key])
            return returnValue(this.cache[key]);
        const value = this.getStorage(persist).getItem(key);
        if (!exists(value))
            return resolveContent(fallbackValue);
        this.cache[key] = JSON.parse(value);
        return returnValue(this.cache[key]);
    }
    query(query) {
        const toRet = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key?.match(query)) {
                const item = localStorage.getItem(key);
                if (item) {
                    try {
                        const exp = JSON.parse(item);
                        toRet.push(exp);
                    }
                    catch (e) {
                        this.logError(e);
                    }
                }
            }
        }
        return toRet;
    }
    deleteAll(query) {
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key?.match(query)) {
                localStorage.removeItem(key);
            }
        }
    }
    withstandDeletion(storageKey) {
        this.withstandDeletionKeys.push(storageKey);
    }
    registerKey(storageKey) {
        this.allKeys[storageKey.key] = storageKey;
    }
}
export const ModuleFE_LocalStorage = new StorageModule_Class();
//TODO Generic Keys like in the tests contexts
export class StorageKey {
    key;
    persist;
    _onChange;
    isSameInstance = false;
    constructor(key, persist = true) {
        this.key = key;
        this.persist = persist;
        // @ts-ignore
        ModuleFE_LocalStorage.registerKey(this);
    }
    /**
     * Will not delete this storage key if the delete operation is performed from __onClearWebsiteData
     */
    withstandDeletion() {
        // @ts-ignore
        ModuleFE_LocalStorage.withstandDeletion(this);
        return this;
    }
    get(fallbackValue) {
        // @ts-ignore
        return ModuleFE_LocalStorage.get(this.key, fallbackValue, this.persist, this.isSameInstance);
    }
    patch(value) {
        const previousValue = this.get();
        const mergedValue = merge(previousValue, value);
        this.set(mergedValue);
        return mergedValue;
    }
    set(value) {
        // @ts-ignore
        ModuleFE_LocalStorage.set(this.key, value, this.persist);
        // console.log('StorageKey', this.key);
        return value;
    }
    delete() {
        ModuleFE_LocalStorage.delete(this.key, this.persist);
    }
    clearCache() {
        ModuleFE_LocalStorage.clearCache(this.key);
    }
    onChange(onChangeListener) {
        this._onChange = () => {
            const before = this.get();
            this.clearCache();
            const after = this.get();
            return onChangeListener(after, before);
        };
        return this;
    }
    getPersistence = () => this.persist;
    /**
     * Sets the isSameInstance flag to true.
     * Any subsequent calls to get will return the same instance as in the cache, instead of a copy
     * @deprecated
     */
    sameInstance = () => {
        this.isSameInstance = true;
        return this;
    };
}
//# sourceMappingURL=ModuleFE_LocalStorage.js.map